import { AgGridReact } from "ag-grid-react";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { getRowId } from "utils/grid-utils";
import { AppContext } from "context/AppProvider";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { resetGridFilters } from "entity-app/utils/utils";
import { ColDef, ColGroupDef, GridApi } from "ag-grid-enterprise";
import { FilterItem } from "models/types";
import SubmittalListFilterComponent from "pages/submittals-list/filter-bar/submittal-list-filter-bar";
import ErrorBoundary from "components/error-boundary";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import { GridStatusMessage } from "components/widgets/grid-status-message";
import { getConditionalFormattingHeaderMenuItems } from "components/conditional-formatting/conditional-formatting-header-menu";
import type { ConditionalFormattingRule } from "components/conditional-formatting";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import { RiskLevelType } from "components/date-block/models";
import { useShowDesignAndSpec } from "hooks/common";
import { matchUserProjectRole } from "utils/utils";
import ImportACCSubmittalLogs from "components/import-acc-submittal-logs";
import ImportSubmittalLogs from "components/import-submittal-logs";
import { Button, Empty, Modal } from "antd";
import { message } from "utils/ciq-message";
import { FeatureTypes } from "entity-app/constants";
import FeatureBulkEditDurationsNewModel from "components/feature-bulk-edit-durations/feature-bulk-edit-durations-new-model";
import { OpenNotification } from "pages/submittals-log-page/components/open-notification";
import SubmittalLogCreateComponentNewModel from "pages/submittal-log-create/submittal-log-create-new-model";
import { InfoFeatureType } from "change-events/change-event-polling";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import {
  ESubmittalStatus,
  EUserTypes,
  ProjectPermissionEnum,
  SidepanelStyles,
  submittalLogFilterTitles,
  SubmittalStatusToStrMap,
  SuccessMessages,
  priorities
} from "constants/index";
import { DateUtils } from "utils/dateutils";
import { getUser } from "services/auth";
import { applySubmittalLogFilters } from "utils/ag-grid-programmatic-filtering";
import {
  PRINT_VIEW_STORAGE_KEY,
  SubmittalLogPrintViewState
} from "pages/print-view-layout/model";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";

import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import CreateExtractMaterialFromSubmittal from "pages/material-extracted-log/components/create-material-from-submittal";
import BulkEditSubmittalDetailsNewModel from "./bulk-edit-submittals/bulk-edit-submittal-details-new-model";

import {
  getSidebarColumnLayout,
  PRESERVE_MILESTONE_NAMED_COLUMN_IDS
} from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import useFeatureLogNavWriter from "hooks/use-feature-log-nav-writer";
import useFeatureSubmittalLogData from "./hooks/submittal-log-data";
import useSubmittalLogGridConfig from "./hooks/submittal-log-grid-config";
import useSubmittalIntegration from "./hooks/submittal-integration";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import MoveUntrackedSubmittalModal from "pages/submittal-log-untracked/modals/move-untracked-submittal-modal";
import {
  getSubmittalLogRoute,
  getSubmittalLogViewFromPath,
  isSubmittalLogRestrictedView,
  SUBMITTAL_LOG_VIEW_TITLES,
  type SubmittalLogView
} from "./submittal-log-view";

function RestrictedSubmittalLogNoRowsOverlay() {
  return (
    <div className="flex h-full w-full items-center justify-center py-10">
      <Empty description="No data" />
    </div>
  );
}

function SubmittalLog({
  logView: logViewProp
}: {
  logView?: SubmittalLogView;
}) {
  const { projectId } = useParams() as { projectId: string };
  const history = useHistory();
  const location = useLocation();
  const logView = logViewProp ?? getSubmittalLogViewFromPath(location.pathname);
  const pageTitle = SUBMITTAL_LOG_VIEW_TITLES[logView];
  const isRestrictedLogView = isSubmittalLogRestrictedView(logView);
  const submittalLogReturnTo = getSubmittalLogRoute(projectId, logView);

  const {
    tokenRetrievalState,
    projectDetails,
    columnHeaders: { submittalHeaderMap },
    tokenContents,
    gqlClientForProject,
    loggedInUserProjectProfileResponse,
    isFeatureFlagEnabled,
    isProjectArchived
  } = useContext(ProjectContext);

  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const { submittalListFilter, setSubmittalListFilter } =
    useContext(AppContext);
  // Dashboard drill-down filters apply only to the main Submittal Log.
  const activeListFilter = isRestrictedLogView ? null : submittalListFilter;

  const isCurrentUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isInlineEditSubmittalPermissionGranted = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.InlineEditSubmittal,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const isViewLinkingPage = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.ViewLinkingPage,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const cannotCreateMaterial = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role ?? ""
  );

  const isGroupMaterialCreationEnabled = isFeatureFlagEnabled(
    "GROUPED_MATERIAL_CREATION"
  );

  const projectToken = tokenRetrievalState?.token;

  const gridRef = useRef<AgGridReact<any>>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const existingSubmittalsCountRef = useRef<any>(null);

  const navWriter = useFeatureLogNavWriter({
    gridRef,
    feature: "submittals",
    projectId
  });

  const {
    submittalList: submittalListFull,
    maxNumberOfMilestones,
    submittalTypesMap,
    statusesMap,
    WFProjectParticipants,
    isIntegrationConfigured,
    isIntegrationMode,
    submittalListLoading,
    submittalListLoadedOnce,
    fetchLogData,
    mutationDeleteSubmittal,
    setOnDataUpdateCallback
  } = useFeatureSubmittalLogData(projectId, projectToken, logView);

  const { currentSourceType } = useSubmittalIntegration({
    skip: isRestrictedLogView
  });

  const conditionalFormattingRulesRef = useRef<ConditionalFormattingRule[]>([]);
  const setConditionalFormattingRulesRef = useRef<
    (rules: ConditionalFormattingRule[]) => void
  >(() => {});

  const {
    gridDefaultColDef,
    gridColumnDefs,
    dateBlockColumnsDefs,
    saveColumnState,
    setColumnVisibility,
    applyColumnConfig,
    isColumnConfigReady,
    onEditCell
  } = useSubmittalLogGridConfig(projectId, gridRef, maxNumberOfMilestones, {
    getConditionalsFormatting: () => conditionalFormattingRulesRef.current,
    onConditionalsFormattingChange: (rules) => {
      conditionalFormattingRulesRef.current = rules;
      setConditionalFormattingRulesRef.current(rules);
    },
    skipAuxiliaryQueries: isRestrictedLogView
  });
  const {
    conditionalFormattingRules,
    conditionalFormattingDefaultColDef,
    conditionalFormattingExcelStyles,
    setConditionalFormattingRules
  } = useConditionalFormatting({
    gridRef,
    defaultColDef: gridDefaultColDef
  });
  conditionalFormattingRulesRef.current = conditionalFormattingRules;
  setConditionalFormattingRulesRef.current = setConditionalFormattingRules;
  const mergedExcelStyles = useMemo(
    () => [...excelStyles, ...conditionalFormattingExcelStyles],
    [conditionalFormattingExcelStyles]
  );

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: () => {
      fetchLogData();
    },
    infoFeatureType: InfoFeatureType.submittal,
    skipParticipants: isRestrictedLogView
  });

  const { isDesignTabEnabled } = useShowDesignAndSpec();

  const [selectedRows, setSelectedRows] = useState<Array<any>>([]);
  const [stats, setStats] = useState<any>("");
  const [displayRowCount, setDisplayRowCount] = useState(0);

  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [searchText, setSearchText] = useState("");

  const [showSubmittalBulkEditDetails, setShowSubmittalBulkEditDetails] =
    useState(false);
  const [showSubmittalBulkEditDurations, setShowSubmittalBulkEditDurations] =
    useState(false);
  const [
    showSubmittalBulkEditDeadlineDate,
    setShowSubmittalBulkEditDeadlineDate
  ] = useState(false);

  const [showExtractMaterial, setShowExtractMaterial] = useState(false);

  const [showUntrackedSubmittals, setShowUntrackedSubmittals] = useState(false);
  const [submittalsToUntrack, setSubmittalsToUntrack] = useState<any[]>([]);

  const [showImportLogDrawer, setshowImportLogDrawer] =
    useState<boolean>(false);

  const [showNewLogDrawer, setshowNewLogDrawer] = useState<boolean>(false);

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const isFirstDataRenderedRef = useRef(false);
  isFirstDataRenderedRef.current = isFirstDataRendered;
  const saveColumnStateRef = useRef(saveColumnState);
  saveColumnStateRef.current = saveColumnState;
  // Default/master column config must apply once. Milestone/date-block rebuilds
  // from list refreshes would otherwise re-apply with stale user config.
  const hasAppliedDefaultColumnConfigRef = useRef(false);

  useEffect(() => {
    if (
      submittalListFull &&
      submittalListFull.length > 0 &&
      setSelectedRows.length > 0
    ) {
      setSelectedRows(gridRef.current?.api?.getSelectedRows() || []);
    }
  }, [submittalListFull]);

  const submittalList = useMemo(() => {
    if (isRestrictedLogView || !submittalListFull) {
      return submittalListFull;
    }
    // Default log: hide deleted-from-source rows (they live on /submittals-deleted).
    return submittalListFull.filter((sub: any) => !sub?.deleted_from_source);
  }, [isRestrictedLogView, submittalListFull]);

  const onAllSubmittalsClick = useCallback(() => {
    resetGridFilters({
      setListFilter: setSubmittalListFilter,
      api: gridRef.current?.api
    });
  }, [setSubmittalListFilter, gridRef]);

  const updateStats = useCallback(() => {
    if (!gridRef.current?.api) return;
    const length = submittalList?.length || 0;
    const displayedRowCount = gridRef.current?.api?.getDisplayedRowCount();
    if (displayedRowCount === undefined) return;

    if (activeListFilter?.key) {
      const titleText = submittalLogFilterTitles[activeListFilter.key];
      const statsContents = (
        <>
          <span>
            {displayedRowCount} of {length}
          </span>
          <span className="px-1">{titleText}</span>
        </>
      );
      setStats(statsContents);
      setDisplayRowCount(displayedRowCount || 0);
      return;
    }

    if (gridRef.current?.api?.isAnyFilterPresent()) {
      setTimeout(() => {
        setStats(`${displayedRowCount} of ${length}`);
        setDisplayRowCount(displayedRowCount || 0);
      }, 500);
      return;
    }

    setStats(`${displayedRowCount}`);
    setDisplayRowCount(displayedRowCount);
  }, [submittalList?.length, activeListFilter]);

  useEffect(() => {
    setOnDataUpdateCallback(() => {
      updateStats();
    });
    return () => {
      setOnDataUpdateCallback(null);
    };
  }, [updateStats, setOnDataUpdateCallback]);

  const handleSaveColumnState = useCallback(() => {
    if (activeListFilter?.key || isRestrictedLogView) return;
    saveColumnState();
  }, [saveColumnState, activeListFilter, isRestrictedLogView]);

  const applySidebarTreeLayout = useCallback(() => {
    if (!gridRef.current?.api) return;
    const milestonesCount = maxNumberOfMilestones?.milestonesCount ?? 0;
    if (milestonesCount <= 0) return;
    const { sidebarColumnLayout } = getSidebarColumnLayout(
      maxNumberOfMilestones?.milestonesCount ?? null,
      maxNumberOfMilestones?.milestoneIndicesWithOffset
    );
    if (typeof gridRef.current.api.getToolPanelInstance !== "function") return;
    const columnsToolPanel =
      gridRef.current.api.getToolPanelInstance?.("columns");
    if (!columnsToolPanel) return;
    const existingLayout =
      (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) || [];
    const filteredExistingLayout = existingLayout.filter(
      (item: ColDef | ColGroupDef) => {
        const colId = (item as ColDef).colId;
        if (!colId) return true;
        if (colId === "_milestones" || colId === "_durations") return false;
        // Remove both legacy and new date-block leaf columns before applying tree layout
        if (colId.startsWith("milestone_")) return false; // legacy
        if (colId.startsWith("offset_")) return false; // legacy
        if (
          colId.includes("milestone_") &&
          !PRESERVE_MILESTONE_NAMED_COLUMN_IDS.has(colId)
        )
          return false; // name_milestone_, planned_milestone_, etc
        if (colId.includes("offset_")) return false; // name_offset_, duration_offset_
        return true;
      }
    );
    columnsToolPanel.setColumnLayout([
      ...filteredExistingLayout,
      ...sidebarColumnLayout
    ]);
    const toolPanelState = (columnsToolPanel as any).getState?.();
    if (toolPanelState) {
      (columnsToolPanel as any).setState?.({
        ...toolPanelState,
        expandedGroupIds: (toolPanelState as any).expandedGroupIds?.filter(
          (id: string) => id !== "milestones_group" && id !== "durations_group"
        )
      });
    }
    (columnsToolPanel as any).collapseColumnGroups?.([
      "milestones_group",
      "durations_group"
    ]);
  }, [maxNumberOfMilestones]);

  const milestoneOffsetIndicesKey =
    maxNumberOfMilestones?.milestoneIndicesWithOffset
      ? Array.from(maxNumberOfMilestones.milestoneIndicesWithOffset)
          .sort((a, b) => a - b)
          .join(",")
      : "";
  useEffect(() => {
    if (!isFirstDataRendered) return;
    const timer = setTimeout(() => applySidebarTreeLayout(), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFirstDataRendered,
    maxNumberOfMilestones?.milestonesCount,
    milestoneOffsetIndicesKey
  ]);

  useEffect(() => {
    if (
      hasAppliedDefaultColumnConfigRef.current ||
      !isFirstDataRendered ||
      !isColumnConfigReady ||
      activeListFilter?.key
    ) {
      return;
    }
    setSearchText(applyColumnConfig({ type: "default" }) ?? "");
    hasAppliedDefaultColumnConfigRef.current = true;
  }, [
    isFirstDataRendered,
    isColumnConfigReady,
    applyColumnConfig,
    activeListFilter
  ]);

  // Empty grids never fire onFirstDataRendered; apply column config once the
  // list fetch and config queries are ready without touching isFirstDataRendered.
  useEffect(() => {
    if (hasAppliedDefaultColumnConfigRef.current) {
      return;
    }
    if (!isColumnConfigReady || activeListFilter?.key) {
      return;
    }
    if (!submittalListLoadedOnce || !gridRef.current?.api) {
      return;
    }
    if ((submittalList?.length ?? 0) > 0) {
      return;
    }
    setSearchText(applyColumnConfig({ type: "default" }) ?? "");
    hasAppliedDefaultColumnConfigRef.current = true;
    if (isRestrictedLogView) {
      gridRef.current.api.showNoRowsOverlay?.();
    }
  }, [
    isColumnConfigReady,
    submittalListLoadedOnce,
    submittalList?.length,
    activeListFilter,
    applyColumnConfig,
    isRestrictedLogView
  ]);

  // Keep Empty hidden under the Loading spinner while the archive fetch runs.
  useEffect(() => {
    if (!isRestrictedLogView || !gridRef.current?.api) return;
    if (submittalListLoading || !submittalListLoadedOnce) {
      gridRef.current.api.hideOverlay?.();
    }
  }, [isRestrictedLogView, submittalListLoading, submittalListLoadedOnce]);

  const onFirstDataRendered = () => {
    const loggedInUserCompany =
      loggedInUserProjectProfileResponse?.data?.subscription_users[0]?.user
        .project_users[0]?.subscription_vendor?.name;

    const milestonesCountForSidebar =
      maxNumberOfMilestones?.milestonesCount ?? null;
    const shouldApplySidebarLayout =
      (maxNumberOfMilestones?.milestonesCount ?? 0) > 0;
    const { sidebarColumnLayout } = shouldApplySidebarLayout
      ? getSidebarColumnLayout(
          milestonesCountForSidebar,
          maxNumberOfMilestones?.milestoneIndicesWithOffset
        )
      : { sidebarColumnLayout: [] };

    const applyTreeLayoutToToolPanel = () => {
      if (typeof gridRef.current?.api?.getToolPanelInstance !== "function")
        return;
      const columnsToolPanel =
        gridRef.current?.api?.getToolPanelInstance?.("columns");
      if (columnsToolPanel && gridRef.current?.api) {
        const existingLayout =
          (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) ||
          [];
        const filteredExistingLayout = existingLayout.filter(
          (item: ColDef | ColGroupDef) => {
            const colId = (item as ColDef).colId;
            if (!colId) return true;
            if (colId === "_milestones" || colId === "_durations") return false;
            if (colId.startsWith("milestone_")) return false; // legacy
            if (colId.startsWith("offset_")) return false; // legacy
            if (
              colId.includes("milestone_") &&
              !PRESERVE_MILESTONE_NAMED_COLUMN_IDS.has(colId)
            )
              return false; // new
            if (colId.includes("offset_")) return false; // new
            return true;
          }
        );
        columnsToolPanel.setColumnLayout(
          sidebarColumnLayout.length
            ? [...filteredExistingLayout, ...sidebarColumnLayout]
            : filteredExistingLayout
        );
        const toolPanelState = (columnsToolPanel as any).getState?.();
        if (toolPanelState) {
          (columnsToolPanel as any).setState?.({
            ...toolPanelState,
            expandedGroupIds: (toolPanelState as any).expandedGroupIds?.filter(
              (id: string) =>
                id !== "milestones_group" && id !== "durations_group"
            )
          });
        }
        (columnsToolPanel as any).collapseColumnGroups?.([
          "milestones_group",
          "durations_group"
        ]);
      }
    };

    setTimeout(applyTreeLayoutToToolPanel, 0);

    const loggedInUserInfo = getUser();
    if (activeListFilter?.key) {
      applySubmittalLogFilters({
        gridRef,
        loggedInUserInfo,
        submittalListFilter: activeListFilter,
        SubmittalStatusToStrMap,
        additionalData: {
          loggedInUserCompany
        }
      });
    } else if (
      !hasAppliedDefaultColumnConfigRef.current &&
      isColumnConfigReady
    ) {
      setSearchText(applyColumnConfig({ type: "default" }) ?? "");
      hasAppliedDefaultColumnConfigRef.current = true;
    }
    updateStats();
    setTimeout(() => {
      setIsFirstDataRendered(true);
    }, 500);
    navWriter.onFirstDataRendered();
  };

  const setFiltersForHeader = useCallback(() => {
    const filtersApplied = gridRef.current?.api?.getFilterModel();
    // get columnDefs from gridRef
    const columnDefs = gridRef.current?.api?.getColumnDefs();
    if (filtersApplied) {
      const items: FilterItem[] = new Array<FilterItem>();
      Object.keys(filtersApplied).forEach((key: any) => {
        const field = columnDefs!.filter((x: any) => x.colId === key)[0];
        const filter = filtersApplied[key];

        items.push({
          field: key,
          header: field ? field.headerName : key,
          filter
        });
      });

      setFilters(items);
    }
  }, []);

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const getColumnStateFromGrid = (prop: { isIncludeSearchText?: boolean }) => {
    const { isIncludeSearchText = true } = prop;
    if (gridRef.current) {
      return UtilColumnConfig.getColumnStateFromGrid({
        gridRef: {
          api: gridRef.current!.api
        },
        isIncludeSearchText,
        conditionals_formatting: conditionalFormattingRulesRef.current
      });
    }
    return null;
  };

  const exportLogAsCSV = () => {
    if (gridRef.current && projectDetails) {
      const filename = `Submittal_Log_${projectDetails.name}.csv`;
      exportEntityLogAsCSV({
        filename,
        gridRef: {
          api: gridRef.current!.api
        },
        includedColumns: ["description"],
        excludedColumns: ["_milestones", "_durations", "action"]
      });

      saveAuditLogEvent({
        eventType: "SUBMITTAL_LOG_EXPORT_CSV",
        featureType: "SUBMITTALS",
        filename
      });
    }
  };

  const exportLogAsExcel = (options: ExcelExportOptions) => {
    if (!gridRef.current || !projectDetails) return;
    const filename: string = `Submittal_log_${sanitizeSheetName(projectDetails.name)}.xlsx`;
    exportEntityLogAsExcel({
      filename,
      projectDetails,
      gridRef,
      prependText: `${
        projectDetails.name
      } - Submittal Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
        `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
      )}`,
      includedColumns: ["description"],
      excludedColumns: ["_milestones", "_durations", "action"],
      conditionalsFormatting: conditionalFormattingRulesRef.current,
      options
    });

    saveAuditLogEvent({
      eventType: "SUBMITTAL_LOG_EXPORT_EXCEL",
      featureType: "SUBMITTALS",
      filename
    });
  };

  const openPrintView = useCallback(() => {
    if (!gridRef.current || !projectDetails) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      sessionStorage.clear();

      const context = gridRef?.current?.props?.context || {};
      const contextData = {
        typesMap: context.typesMap,
        statusesMap: context.statusesMap,
        tokenContents: context.tokenContents,
        projectDetails: context.projectDetails,
        projectParticipants: context.projectParticipants,
        isIntegrationMode: context.isIntegrationMode,
        isCurrentUserGC: context.isCurrentUserGC,
        isDesignTabEnabled: context.isDesignTabEnabled,
        isViewLinkingPage: context.isViewLinkingPage,
        featureHeaderMap: context.featureHeaderMap,
        canInlineEditDBOffset: context.canInlineEditDBOffset,
        priorities: context.priorities,
        projectId: context.projectId,
        isGroupMaterialCreationEnabled: context.isGroupMaterialCreationEnabled,
        cannotCreateMaterial: context.cannotCreateMaterial
      };

      const initialState = gridRef.current.api.getState();
      const printViewState: SubmittalLogPrintViewState = {
        pageId: "submittal-log",
        title: `${
          projectDetails?.name || "Project"
        } - Submittal Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState,
        context: contextData,
        skipColumnIds: ["action"],
        headerHeight: gridRef.current.props.headerHeight,
        rowHeight: gridRef.current.props.rowHeight,
        conditionalsFormatting: conditionalFormattingRulesRef.current
      };

      sessionStorage.setItem(
        PRINT_VIEW_STORAGE_KEY,
        JSON.stringify(printViewState)
      );

      const printViewUrl = `/project/${projectDetails.id}/printView`;
      window.open(printViewUrl, "_blank");
    } catch (error) {
      console.error("Error opening material log print view:", error);
      message.error("An error occurred while opening print view.");
    }
  }, [projectDetails]);

  const onSubmittalsEdit = (
    panel:
      | "details"
      | "duration"
      | "deadline_date"
      | "extract_material"
      | "untracked"
  ) => {
    if (panel === "details") {
      setShowSubmittalBulkEditDetails(true);
    }
    if (panel === "duration") {
      setShowSubmittalBulkEditDurations(true);
    }
    if (panel === "deadline_date") {
      setShowSubmittalBulkEditDeadlineDate(true);
    }
    if (panel === "extract_material") {
      setShowExtractMaterial(true);
    }
    if (panel === "untracked") {
      setSubmittalsToUntrack(selectedRows || []);
      setShowUntrackedSubmittals(true);
    }
  };

  const handleDeleteSubmittal = useCallback(
    async (submittalId: string[]) => {
      const deletedSubmittalsRes = await mutationDeleteSubmittal({
        variables: {
          feature_instance_ids: submittalId,
          feature_type_id: 1
        }
      });
      if (deletedSubmittalsRes.data) {
        message.success(SuccessMessages.deleteSuccess);
        fetchLogData();
      }
      if (deletedSubmittalsRes.errors) {
        console.error(deletedSubmittalsRes.errors);
        message.error("Failed to delete Submittals.");
      }
    },
    [mutationDeleteSubmittal, fetchLogData]
  );

  const headerComponentViewModel: any = {
    gridRef,
    items: filters,
    onCreateLogClick: () => {
      setshowNewLogDrawer(true);
    },
    onImportLogClick: () => {
      existingSubmittalsCountRef.current = submittalList;
      setshowImportLogDrawer(true);
    },
    onSearch,
    searchText,
    stats,
    showFiterChips: !activeListFilter,
    onAllSubmittalsClick,
    selectedRow: selectedRows,
    onExportCSV: exportLogAsCSV,
    onExportExcel: exportLogAsExcel,
    onOpenPrintView: () => {
      if (gridRef.current)
        PrintViewValidationModal({
          gridApi: gridRef.current.api,
          onProceed: openPrintView,
          skipColumnIds: ["action", "ag-Grid-SelectionColumn"]
        });
    },
    onSubmittalEditClick: onSubmittalsEdit,
    isIntegrationMode,
    displayRowCount,
    isIntegrationConfigured,
    resetFilters: () => {
      gridRef?.current?.api?.setFilterModel(null);
      gridRef?.current?.api?.setGridOption("quickFilterText", "");
      setSearchText("");
    },
    getColumnStateFromGrid,
    setFilterStateFromData: (columnState: any) => {
      applyColumnConfig({ type: "custom", columnConfig: columnState });
      setSearchText(columnState?.quickSearch ?? "");
    },
    afterApplyFilterCallBack: () => {
      setTimeout(() => {
        applySidebarTreeLayout();
      }, 0);
    },
    disabled: false,
    isGridDataRendered: false,
    handleDeleteSubmittal,
    onSubmittalDeleted: () => {
      fetchLogData();
    },
    logView,
    pageTitle
  };

  const showRestrictedEmptyState =
    isRestrictedLogView &&
    submittalListLoadedOnce &&
    !submittalListLoading &&
    (submittalList?.length ?? 0) === 0;

  if (activeListFilter && loggedInUserProjectProfileResponse?.loading) {
    return <div />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <CiqGridViewWrapper
        additionalGridComponent={
          <GridStatusMessage
            {...{
              isIntegrationMode,
              isIntegrationConfigured,
              submittalListLoading:
                submittalListLoading ||
                (submittalList?.length > 0 && !isFirstDataRendered),
              submittalListData: isFirstDataRendered ? submittalList : null,
              submittalListLoadedOnce,
              projectId,
              useAgGridEmptyState: isRestrictedLogView
            }}
          />
        }
        headerComponent={
          <ErrorBoundary>
            <SubmittalListFilterComponent {...headerComponentViewModel} />
          </ErrorBoundary>
        }
        gridInfo={{
          gridRef,
          defaultColDef: conditionalFormattingDefaultColDef,
          getMainMenuItems: getConditionalFormattingHeaderMenuItems,
          columnDefs: gridColumnDefs as any,
          rowData: submittalList,
          rowHeight: 62,
          getRowId,
          excelStyles: mergedExcelStyles,
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest: (event: any) => {
            onCellEditRequest(event);
          },
          onFirstDataRendered,
          onRowDataUpdated: () => {
            updateStats();
            navWriter.onModelUpdated();
          },
          onSortChanged: (e: any) => {
            if (
              !activeListFilter &&
              !isRestrictedLogView &&
              e.source === "uiColumnSorted"
            ) {
              handleSaveColumnState();
            }
            if (!isRestrictedLogView) {
              setFiltersForHeader();
            }
            navWriter.onSortChanged();
          },
          onFilterChanged: () => {
            filterChipRef.current?.onFilterApplied();
            if (!isRestrictedLogView) {
              handleSaveColumnState();
              setFiltersForHeader();
            }
            updateStats();
            navWriter.onFilterChanged();
          },
          onColumnVisible: (e: any) => {
            if (e.source === "toolPanelUi") {
              const hasMilestoneCol = e?.columns.find(
                (column: any) => column.colId === "_milestones"
              );

              const hasDurationCol = e?.columns.find(
                (column: any) => column.colId === "_durations"
              );

              if (hasMilestoneCol) {
                setColumnVisibility(
                  dateBlockColumnsDefs.dateBlockColumnIds.milestones as any,
                  e.visible
                );
              }

              if (hasDurationCol) {
                setColumnVisibility(
                  dateBlockColumnsDefs.dateBlockColumnIds.durations as any,
                  e.visible
                );
              }
              handleSaveColumnState();
            }
          },
          onDragStopped: () => {
            if (!activeListFilter && !isRestrictedLogView) {
              handleSaveColumnState();
              setTimeout(() => {
                applySidebarTreeLayout();
              }, 0);
            }
          },
          onToolPanelVisibleChanged: (e: any) => {
            if (e?.key === "columns" && e?.visible) {
              setTimeout(() => applySidebarTreeLayout(), 0);
            }
          },
          onNewColumnsLoaded: () => {
            setTimeout(() => applySidebarTreeLayout(), 0);
          },
          context: {
            conditionalFormattingRules,
            onConditionalFormattingRulesChange: (
              rules: ConditionalFormattingRule[]
            ) => {
              conditionalFormattingRulesRef.current = rules;
              setConditionalFormattingRules(rules);
              if (isFirstDataRenderedRef.current && !isRestrictedLogView) {
                saveColumnStateRef.current();
              }
            },
            conditionalFormattingColumnConfig: {
              risk_level: {
                filterType: "set",
                options: [
                  RiskLevelType.High,
                  RiskLevelType.Medium,
                  RiskLevelType.Low
                ]
              },
              status: {
                filterType: "set",
                options: Object.values(SubmittalStatusToStrMap)
              },
              state: {
                filterType: "set",
                options: ["Draft", "Open", "Closed"]
              }
            },
            typesMap: submittalTypesMap,
            statusesMap,
            tokenContents: tokenRetrievalState?.tokenContents,
            projectDetails,
            projectParticipants: WFProjectParticipants,
            isIntegrationMode,
            onEditCell,
            onViewSpecSection: () => {},
            isCurrentUserGC,
            isDesignTabEnabled,
            isViewLinkingPage,
            featureHeaderMap: submittalHeaderMap,
            canInlineEditDBOffset: isInlineEditSubmittalPermissionGranted,
            priorities,
            projectId,
            history,
            isGridCellEditable: (params: any) => {
              const editable = params?.colDef?.editable;
              if (typeof editable === "function") {
                return editable(params);
              }
              return editable;
            },
            isGroupMaterialCreationEnabled,
            onSubmittalEditClick: onSubmittalsEdit,
            openMoveUntrackedModal: (submittals: any[]) => {
              if (!submittals?.length) return;
              setSubmittalsToUntrack(submittals);
              setShowUntrackedSubmittals(true);
            },
            cannotCreateMaterial,
            isProjectArchived,
            submittalLogReturnTo
          },
          onSelectionChanged: ({ api }: { api: GridApi }) => {
            setSelectedRows(api.getSelectedRows());
          },
          tooltipShowMode: "whenTruncated",
          ...(isRestrictedLogView
            ? {
                // Hide Empty while fetch is in flight so it doesn't sit under Loading.
                suppressNoRowsOverlay: !showRestrictedEmptyState,
                noRowsOverlayComponent: RestrictedSubmittalLogNoRowsOverlay
              }
            : {
                overlayNoRowsTemplate: "",
                suppressNoRowsOverlay: true
              }),
          loading: false,
          // Must override CiqAgGrid defaults — omitting rowSelection leaves its checkboxes on.
          ...(isRestrictedLogView
            ? {
                rowSelection: {
                  mode: "multiRow" as const,
                  checkboxes: false,
                  headerCheckbox: false,
                  enableClickSelection: false
                },
                selectionColumnDef: undefined
              }
            : {
                rowSelection: {
                  mode: "multiRow" as const,
                  selectAll: "filtered" as const,
                  isRowSelectable: (node: any) =>
                    node?.data?.workflow_status.id !== ESubmittalStatus.VOID &&
                    !node?.data?.deleted_from_source
                },
                selectionColumnDef: {
                  sortable: true,
                  resizable: false,
                  width: 40,
                  suppressHeaderMenuButton: true,
                  pinned: "left" as const,
                  lockPinned: true
                }
              }),
          suppressAggFuncInHeader: true,
          suppressDragLeaveHidesColumns: true,
          maintainColumnOrder: true,
          enableCellTextSelection: true,
          onColumnResized: (params: any) => {
            if (params.finished) {
              // User has finished resizing, do something like save column state
              handleSaveColumnState();
            }
          },
          disableResizingAutoWidth: true
        }}
      />

      {showSubmittalBulkEditDetails && (
        <BulkEditSubmittalDetailsNewModel
          onCancelClick={() => {
            setShowSubmittalBulkEditDetails(false);
          }}
          onUpdateDone={() => {
            setShowSubmittalBulkEditDetails(false);
            gridRef?.current?.api?.deselectAll();
            fetchLogData();
          }}
          selectedRows={selectedRows}
          gqlClient={gqlClientForProject}
          setShowSubmittalEdit={setShowSubmittalBulkEditDetails}
          showSubmittalEdit={showSubmittalBulkEditDetails}
          projectId={projectId}
          isIntegrationMode={isIntegrationMode}
        />
      )}

      {selectedRows && showSubmittalBulkEditDeadlineDate && (
        <BulkEditDeadlineDateModal
          onUpdateDone={() => {
            setShowSubmittalBulkEditDeadlineDate(false);
            gridRef?.current?.api?.deselectAll();
            fetchLogData();
          }}
          setShowEdit={setShowSubmittalBulkEditDeadlineDate}
          showEdit={showSubmittalBulkEditDeadlineDate}
          selectedRows={selectedRows}
          columnHeaderMap={submittalHeaderMap}
          featureTypeId={FeatureTypes.SUBMITTALS}
        />
      )}

      {selectedRows?.length > 0 && showExtractMaterial && (
        <CreateExtractMaterialFromSubmittal
          selectedRows={selectedRows}
          onCancel={() => setShowExtractMaterial(false)}
          onComplete={() => {
            setShowExtractMaterial(false);
            gridRef?.current?.api?.deselectAll();
          }}
          openDrawer={showExtractMaterial}
        />
      )}

      {showImportLogDrawer &&
        (isIntegrationMode ? (
          <ImportACCSubmittalLogs
            tokenRetrievalState={tokenRetrievalState}
            gqlClientForProject={gqlClientForProject}
            showImportLogDrawer={showImportLogDrawer}
            setDrawerOpen={setshowImportLogDrawer}
            onCancel={() => {
              setshowImportLogDrawer(false);
            }}
            currentSourceType={currentSourceType}
          />
        ) : (
          <ImportSubmittalLogs
            submittalHeaderMap={submittalHeaderMap}
            tokenRetrievalState={tokenRetrievalState}
            gqlClientForProject={gqlClientForProject}
            existingSubmittalsCount={existingSubmittalsCountRef.current}
            showImportLogDrawer={showImportLogDrawer}
            setDrawerOpen={setshowImportLogDrawer}
            onCancel={() => {
              setshowImportLogDrawer(false);
            }}
          />
        ))}

      {showNewLogDrawer && (
        <SubmittalLogCreateComponentNewModel
          onCancelClick={() => {
            setshowNewLogDrawer(false);
          }}
          onSubmittalCreated={(id: string, title: string) => {
            setshowNewLogDrawer(false);
            OpenNotification({
              projectId,
              id,
              title,
              placement: "top"
            });
          }}
          gqlClient={gqlClientForProject}
          showCreateSubModel={showNewLogDrawer}
          setDrawerOpen={setshowNewLogDrawer}
        />
      )}
      {showUntrackedSubmittals && (
        <MoveUntrackedSubmittalModal
          open={showUntrackedSubmittals}
          featureInstances={submittalsToUntrack}
          onClose={() => {
            setShowUntrackedSubmittals(false);
            setSubmittalsToUntrack([]);
          }}
          onSuccess={() => {
            setShowUntrackedSubmittals(false);
            setSubmittalsToUntrack([]);
            gridRef?.current?.api?.deselectAll();
            fetchLogData();
          }}
        />
      )}

      <Modal
        title="Bulk Edit Durations"
        className="custom-drawer with-padding-fix"
        style={SidepanelStyles.style}
        width="calc(100vw - 200px)"
        footer={[
          <div className="flex w-full space-x-3 justify-end" key={100}>
            <Button
              onClick={() => {
                setShowSubmittalBulkEditDurations(false);
              }}
              key={101}
            >
              Close
            </Button>
          </div>
        ]}
        open={showSubmittalBulkEditDurations}
        onCancel={() => {
          setShowSubmittalBulkEditDurations(false);
        }}
        destroyOnHidden
        keyboard={false}
      >
        <ErrorBoundary>
          <FeatureBulkEditDurationsNewModel
            selectedRows={selectedRows!}
            featureTypeId={FeatureTypes.SUBMITTALS}
            onUpdateDone={() => {
              fetchLogData();
            }}
          />
        </ErrorBoundary>
      </Modal>
    </div>
  );
}

export default SubmittalLog;
